/** Состояние разрешения на камеру до запуска: `prompt` — браузер ещё спросит пользователя. */
export type CameraPermission = "granted" | "prompt" | "denied";

/** Почему камера не запустилась: пользователь запретил её или камеры нет (нет устройства, не HTTPS). */
export type CameraFailure = "denied" | "unavailable";

/** Задняя камера телефона или фронтальная. */
export type CameraFacing = "environment" | "user";

export class CameraError extends Error {
  constructor(readonly reason: CameraFailure) {
    super(`Camera ${reason}`);
    this.name = "CameraError";
  }
}

export type QrScanOptions = {
  /** Куда выводится изображение с камеры. */
  video: HTMLVideoElement;
  /** Вызывается на каждый распознанный QR, пока сканирование не остановлено. */
  onCode: (text: string) => void;
  facing: CameraFacing;
};

/** Запущенное сканирование одной камерой. */
export type QrScan = {
  /** Браузер умеет включать фонарик этой камеры (`torch`). */
  torch: boolean;
  setTorch: (on: boolean) => Promise<void>;
  /** Камер больше одной: есть что переключать. */
  canSwitchCamera: boolean;
  /** Выключает камеру и распознавание. */
  stop: () => void;
};

/** Камера и распознавание QR. В тестах подменяется фейком, «распознающим» нужную строку. */
export type QrSource = {
  permission: () => Promise<CameraPermission>;
  /** Включает камеру и непрерывно распознаёт QR. Ошибка `CameraError` — камеру не дали или её нет. */
  start: (options: QrScanOptions) => Promise<QrScan>;
};

// Чаще нет смысла: распознавание кадра jsQR на телефоне занимает десятки миллисекунд.
const SCAN_INTERVAL_MS = 200;
// jsQR читает кадр целиком: уменьшенный кадр распознаётся быстрее, а QR с экрана E-va остаётся читаемым.
const MAX_FRAME_SIDE = 640;

/** Камера через `getUserMedia`, распознавание через `BarcodeDetector`, где он есть, иначе через `jsQR`. */
export function createQrSource(): QrSource {
  return {
    async permission() {
      try {
        const status = await navigator.permissions.query({ name: "camera" as PermissionName });
        return status.state;
      } catch {
        // Safari до 16 и Firefox не знают имени "camera": узнаем при запуске.
        return "prompt";
      }
    },

    async start({ video, onCode, facing }) {
      const stream = await openCamera(facing);
      const [track] = stream.getVideoTracks();
      let stopped = false;
      const stop = () => {
        stopped = true;
        for (const streamTrack of stream.getTracks()) streamTrack.stop();
        video.srcObject = null;
      };
      try {
        video.srcObject = stream;
        await video.play();
        const decode = await createDecoder();
        const scanLoop = async () => {
          if (stopped) return;
          const text = await decode(video).catch(() => null);
          if (stopped) return;
          if (text) onCode(text);
          setTimeout(scanLoop, SCAN_INTERVAL_MS);
        };
        void scanLoop();
        return {
          torch: track?.getCapabilities?.().torch === true,
          async setTorch(on) {
            await track?.applyConstraints({ advanced: [{ torch: on }] });
          },
          canSwitchCamera: await hasSeveralCameras(),
          stop,
        };
      } catch (error) {
        stop();
        throw error;
      }
    },
  };
}

async function openCamera(facing: CameraFacing): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) throw new CameraError("unavailable");
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: false,
      // По facingMode, а не по deviceId: у телефонов с несколькими задними модулями перебор устройств
      // попадает на ультраширокую камеру, которая не фокусируется на QR вблизи.
      video: { facingMode: { ideal: facing } },
    });
  } catch (error) {
    const denied = error instanceof DOMException && (error.name === "NotAllowedError" || error.name === "SecurityError");
    throw new CameraError(denied ? "denied" : "unavailable");
  }
}

async function hasSeveralCameras(): Promise<boolean> {
  const devices = await navigator.mediaDevices.enumerateDevices().catch((): MediaDeviceInfo[] => []);
  return devices.filter((device) => device.kind === "videoinput").length > 1;
}

type Decode = (video: HTMLVideoElement) => Promise<string | null>;

async function createDecoder(): Promise<Decode> {
  if (typeof BarcodeDetector !== "undefined") {
    const formats = await BarcodeDetector.getSupportedFormats().catch((): string[] => []);
    if (formats.includes("qr_code")) {
      const detector = new BarcodeDetector({ formats: ["qr_code"] });
      return async (video) => (await detector.detect(video))[0]?.rawValue ?? null;
    }
  }
  // jsQR нужен только браузерам без BarcodeDetector (Safari, Firefox) и грузится отдельным чанком.
  const { default: jsQR } = await import("jsqr");
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) throw new CameraError("unavailable");
  return async (video) => {
    if (!video.videoWidth || !video.videoHeight) return null;
    const scale = Math.min(1, MAX_FRAME_SIDE / Math.max(video.videoWidth, video.videoHeight));
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const frame = context.getImageData(0, 0, canvas.width, canvas.height);
    return jsQR(frame.data, frame.width, frame.height, { inversionAttempts: "dontInvert" })?.data ?? null;
  };
}

// BarcodeDetector и фонарик есть не во всех браузерах, поэтому их нет и в lib.dom.
declare global {
  interface MediaTrackCapabilities {
    torch?: boolean;
  }
  interface MediaTrackConstraintSet {
    torch?: boolean;
  }
  var BarcodeDetector:
    | {
        new (options: { formats: string[] }): { detect: (source: CanvasImageSource) => Promise<{ rawValue: string }[]> };
        getSupportedFormats: () => Promise<string[]>;
      }
    | undefined;
}
